import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Tooltip,
  Avatar,
  AvatarGroup,
  Chip,
  IconButton,
  Tabs,
  Tab,
  Button,
} from '@mui/material';
import { DataGrid, GridColDef, GridRowModel } from '@mui/x-data-grid';
import { AudioPlayer } from '@/common/components/projectDetails/components/AudioPlayer';
import {
  ArrowForwardRounded,
  FolderOutlined,
  GraphicEqRounded,
  LayersOutlined,
} from '@mui/icons-material';
import { GetProjectsQueryData } from '@/generated/graphql';
import { formatDateWithTime } from '@/common/utils/fileUtils';
import {
  ResourceButton,
  ResourceIcon,
} from '@/common/components/ui/LibraryPrimitives';
import {
  ProjectsNoResults,
  ProjectsTableToolbar,
} from './ProjectsTableToolbar';
import { useProjectLibraryStatus } from '../hooks/useProjectLibraryStatus';
import { ProjectListLaunchButton } from './ProjectListLaunchButton';
import {
  matchesLibraryFilter,
  type LibraryFilter,
} from '../utils/projectLibraryFilters';

type ProjectListItem = GetProjectsQueryData['projects'][number];

const formatRelativeDate = (dateString: string): string => {
  const diffDays = Math.floor(
    (Date.now() - new Date(dateString).getTime()) / 86_400_000
  );
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`;
  return `${Math.floor(diffDays / 365)}y ago`;
};

interface ProjectsListProps {
  projects: ProjectListItem[];
  onProjectSelect: (projectId: string) => void;
  onRefreshProjects?: () => Promise<unknown>;
}

const ProjectsList: React.FC<ProjectsListProps> = ({
  projects,
  onProjectSelect,
  onRefreshProjects,
}) => {
  const [filter, setFilter] = useState<LibraryFilter>('all');
  const [refreshError, setRefreshError] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 50,
  });
  const library = useProjectLibraryStatus(projects, filter);
  const filteredProjects = useMemo(
    () =>
      projects.filter((project) =>
        matchesLibraryFilter(filter, library.statuses[project._id])
      ),
    [projects, filter, library.statuses]
  );
  const unverifiedCount =
    filter === 'all' || library.loading
      ? 0
      : projects.filter((project) => {
          const status = library.statuses[project._id];
          return filter === 'outdated'
            ? status?.sync !== 'outdated' && status?.sync !== 'synced'
            : !status || status.kind === 'unknown';
        }).length;
  const handleOpenKeyDown = useCallback(
    (event: React.KeyboardEvent, projectId: string) => {
      if (event.key === 'Enter' || event.key === ' ') {
        // The grid handles these keys too, which otherwise cancels the
        // button's native activation while moving focus between cells.
        event.preventDefault();
        event.stopPropagation();
        onProjectSelect(projectId);
      }
    },
    [onProjectSelect]
  );
  const rows = useMemo(
    () =>
      filteredProjects.map((project) => ({
        id: project._id,
        project,
        isStem: library.statuses[project._id]?.kind === 'stem',
        name: project?.name || '-',
        description: project?.description || '',
        updatedAt: project?.updatedAt || '-',
        previewFile: project?.previewFile || null,
        versionCount:
          project.versionCount ?? project.versionHistory?.length ?? 0,
        memberNames:
          project?.members
            ?.map((member) => member?.artistName || '')
            .filter(Boolean) || [],
      })) as GridRowModel[],
    [filteredProjects, library.statuses]
  );

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'launch',
        headerName: '',
        width: 52,
        resizable: false,
        sortable: false,
        filterable: false,
        hideable: false,
        disableColumnMenu: true,
        getApplyQuickFilterFn: () => null,
        renderCell: (params) => (
          <ProjectListLaunchButton
            project={params.row.project}
            isStem={params.row.isStem}
            tabIndex={params.tabIndex}
          />
        ),
      },
      {
        field: 'name',
        headerName: 'Project',
        flex: 1,
        minWidth: 220,
        hideable: false,
        getApplyQuickFilterFn: (value: string) => {
          const query = value.toLocaleLowerCase();
          return (_value, row) =>
            `${row.name} ${row.description}`
              .toLocaleLowerCase()
              .includes(query);
        },
        renderCell: (params) => (
          <Tooltip
            title={params.row.name}
            placement="bottom-start"
            enterDelay={600}
          >
            <ResourceButton
              aria-label={`Open ${params.row.name}`}
              tabIndex={params.tabIndex}
              onKeyDown={(event) =>
                handleOpenKeyDown(event, String(params.row.id))
              }
              onClick={(event) => {
                event.stopPropagation();
                onProjectSelect(String(params.row.id));
              }}
            >
              <ResourceIcon className="project-icon">
                <FolderOutlined sx={{ fontSize: 20 }} />
              </ResourceIcon>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    fontSize: 13,
                    lineHeight: 1.35,
                    color: 'text.primary',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {params.row.name}
                </Typography>
                {params.row.description && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      lineHeight: 1.3,
                      mt: 0.5,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontSize: 11.5,
                      display: 'block',
                    }}
                  >
                    {params.row.description}
                  </Typography>
                )}
              </Box>
            </ResourceButton>
          </Tooltip>
        ),
      },
      {
        field: 'previewFile',
        getApplyQuickFilterFn: () => null,
        headerName: 'Preview',
        width: 204,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          if (!params.value?.waveForm) {
            return (
              <Tooltip
                title="No audio preview is available for this project yet."
                placement="top"
                enterDelay={200}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    color: 'text.secondary',
                  }}
                >
                  <GraphicEqRounded sx={{ fontSize: 17, opacity: 0.6 }} />
                  <Typography variant="caption" sx={{ fontSize: 11.5 }}>
                    No preview
                  </Typography>
                </Box>
              </Tooltip>
            );
          }
          return (
            <Box onClick={(e) => e.stopPropagation()} sx={{ width: '100%' }}>
              <AudioPlayer
                waveForm={params.value.waveForm}
                projectId={params.row.id}
                fileId={params.value._id}
                variant="inline"
                label={`preview for ${params.row.name}`}
                tabIndex={params.tabIndex}
              />
            </Box>
          );
        },
      },
      {
        field: 'updatedAt',
        getApplyQuickFilterFn: () => null,
        headerName: 'Updated',
        width: 108,
        renderCell: (params) => (
          <Tooltip title={formatDateWithTime(params.value)} placement="top">
            <Box sx={{ fontVariantNumeric: 'tabular-nums' }}>
              <Typography
                variant="body2"
                sx={{ fontSize: 12, lineHeight: 1.5 }}
              >
                {new Date(params.value).toLocaleDateString(undefined, {
                  month: 'short',
                  day: 'numeric',
                })}
              </Typography>
              <Typography
                variant="caption"
                sx={{ display: 'block', fontSize: 11, color: 'text.secondary' }}
              >
                {formatRelativeDate(params.value)}
              </Typography>
            </Box>
          </Tooltip>
        ),
      },
      {
        field: 'versionCount',
        getApplyQuickFilterFn: () => null,
        headerName: 'Versions',
        type: 'number',
        width: 96,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => (
          <Chip
            variant="soft"
            label={params.value}
            icon={<LayersOutlined />}
            aria-label={`${params.value} ${params.value === 1 ? 'version' : 'versions'}`}
            size="small"
            sx={{
              height: 26,
              minWidth: 48,
              borderRadius: '7px',
              fontSize: 11,
              fontWeight: 600,
              fontVariantNumeric: 'tabular-nums',
              '& .MuiChip-label': { px: 0.8 },
              '& .MuiChip-icon': {
                fontSize: 14,
                color: 'text.secondary',
                ml: 0.8,
              },
            }}
          />
        ),
      },
      {
        field: 'memberNames',
        getApplyQuickFilterFn: () => null,
        headerName: 'Members',
        width: 136,
        sortable: false,
        renderCell: (params) => {
          const names: string[] = params.value || [];
          if (names.length === 0)
            return (
              <Typography variant="caption" color="text.secondary">
                —
              </Typography>
            );
          return (
            <Tooltip title={names.join(', ')} placement="top">
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  gap: 1,
                  width: '100%',
                }}
              >
                <AvatarGroup
                  max={3}
                  sx={{
                    '& .MuiAvatar-root': {
                      width: 28,
                      height: 28,
                      fontSize: '0.65rem',
                      fontWeight: 700,
                    },
                  }}
                >
                  {names.map((name: string, i: number) => (
                    <Avatar key={`${name}-${i}`}>
                      {name
                        .trim()
                        .split(/\s+/)
                        .slice(0, 2)
                        .map((part) => part[0])
                        .join('')
                        .toUpperCase()}
                    </Avatar>
                  ))}
                </AvatarGroup>
                {names.length === 1 && (
                  <Typography
                    variant="caption"
                    noWrap
                    sx={{ fontSize: 11.5, color: 'text.secondary' }}
                  >
                    {names[0]}
                  </Typography>
                )}
              </Box>
            </Tooltip>
          );
        },
      },
      {
        field: '__chevron',
        headerName: '',
        width: 48,
        minWidth: 48,
        hideable: false,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <IconButton
            className="row-chevron"
            aria-label={`Open details for ${params.row.name}`}
            tabIndex={params.tabIndex}
            onKeyDown={(event) =>
              handleOpenKeyDown(event, String(params.row.id))
            }
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              onProjectSelect(String(params.row.id));
            }}
          >
            <ArrowForwardRounded sx={{ fontSize: 16 }} />
          </IconButton>
        ),
      },
    ],
    [onProjectSelect, handleOpenKeyDown]
  );

  const handleRowClick = useCallback(
    (params: { row: GridRowModel }) => {
      onProjectSelect(String(params.row.id));
    },
    [onProjectSelect]
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        width: '100%',
      }}
    >
      <Tabs
        value={filter}
        onChange={(_, value: LibraryFilter) => {
          setFilter(value);
          setRefreshError(false);
          setPaginationModel((previous) => ({ ...previous, page: 0 }));
        }}
        aria-label="Project filters"
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          mb: 1,
          '& .MuiTab-root': {
            minHeight: 48,
            px: 2,
            fontSize: 13,
            fontWeight: 600,
          },
        }}
      >
        {(['all', 'daw', 'stem', 'outdated'] as const).map((value) => (
          <Tab
            key={value}
            value={value}
            label={value.toUpperCase()}
            id={`project-filter-${value}`}
            aria-controls="project-filter-results"
          />
        ))}
      </Tabs>
      {filter !== 'all' && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 0.5,
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            role="status"
            sx={{ flex: 1 }}
          >
            {refreshError
              ? 'Could not refresh the project list. Try rechecking when the connection is restored.'
              : library.loading
                ? filter === 'outdated'
                  ? 'Checking linked folders against the latest cloud version…'
                  : 'Checking project types…'
                : unverifiedCount > 0
                  ? `${unverifiedCount} ${unverifiedCount === 1 ? 'project' : 'projects'} ${
                      filter === 'outdated'
                        ? 'could not be verified. Connect the desktop app and link each project’s local folder; then recheck.'
                        : 'could not be classified from the latest-version files.'
                    }`
                  : filter === 'outdated'
                    ? 'Projects with local or cloud changes that need syncing.'
                    : filter === 'daw'
                      ? 'Projects containing a DAW session file.'
                      : 'Audio or MIDI projects without a DAW session file.'}
          </Typography>
          <Button
            size="small"
            disabled={library.loading}
            onClick={async () => {
              try {
                setRefreshError(false);
                await onRefreshProjects?.();
                library.refresh();
              } catch {
                setRefreshError(true);
              }
            }}
          >
            Recheck
          </Button>
        </Box>
      )}
      <Box
        id="project-filter-results"
        role="tabpanel"
        aria-labelledby={`project-filter-${filter}`}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          minWidth: 0,
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          aria-label="All projects"
          rowHeight={76}
          columnHeaderHeight={44}
          showToolbar
          loading={library.loading}
          slots={{
            toolbar: ProjectsTableToolbar,
            noResultsOverlay: ProjectsNoResults,
            noRowsOverlay: () => (
              <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                {filter === 'outdated'
                  ? 'No confirmed out-of-date projects.'
                  : filter === 'all'
                    ? 'No projects yet.'
                    : `No ${filter.toUpperCase()} projects found.`}
              </Box>
            ),
          }}
          slotProps={{
            columnsManagement: {
              getTogglableColumns: (gridColumns) =>
                gridColumns
                  .filter((column) => column.hideable !== false)
                  .map((column) => column.field),
            },
          }}
          onRowClick={handleRowClick}
          disableRowSelectionOnClick
          disableColumnFilter
          disableColumnMenu
          hideFooterSelectedRowCount
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          initialState={{
            sorting: {
              sortModel: [{ field: 'updatedAt', sort: 'desc' }],
            },
          }}
          pageSizeOptions={[25, 50, 100]}
          sx={{ width: '100%', height: 'auto' }}
        />
      </Box>
    </Box>
  );
};

export default React.memo(ProjectsList);
